import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, Truck, UserCog, Package, Wallet, FileText, FileMinus, ArrowLeftRight, BarChart3, FolderKanban, Settings as SettingsIcon, LogOut, ShieldCheck, HardHat, ClipboardList, CalendarDays, DatabaseBackup, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import AiChatBubble from "@/components/AiChatBubble";
import WorkshopUstaSwitcher from "@/components/WorkshopUstaSwitcher";

const ADMIN_NAV = [
  { to: "/", label: "Gösterge Paneli", icon: LayoutDashboard, end: true },
  { to: "/musteriler", label: "Müşteriler", icon: Users },
  { to: "/tedarikciler", label: "Tedarikçiler", icon: Truck },
  { to: "/calisanlar", label: "Çalışanlar", icon: UserCog },
  { to: "/urunler", label: "Ürünler", icon: Package },
  { to: "/kasalar", label: "Kasalar", icon: Wallet },
  { to: "/projeler", label: "Projeler", icon: FolderKanban },
  { to: "/takvim", label: "Takvim", icon: CalendarDays },
  { to: "/ai-planlama", label: "AI Planlama", icon: Sparkles },
  { to: "/satis-faturalari", label: "Satış Faturaları", icon: FileText },
  { to: "/alis-faturalari", label: "Alış Faturaları", icon: FileMinus },
  { to: "/hareketler", label: "Cari Hareketler", icon: ArrowLeftRight },
  { to: "/analiz", label: "Analiz", icon: BarChart3 },
  { to: "/kullanicilar", label: "Kullanıcılar", icon: ShieldCheck },
  { to: "/ayarlar", label: "Ayarlar", icon: SettingsIcon },
  { to: "/yedekleme", label: "Yedekleme", icon: DatabaseBackup },
];

const USTA_NAV = [
  { to: "/projeler", label: "Projeler", icon: FolderKanban, end: true },
  { to: "/islerim", label: "İşlerim", icon: ClipboardList },
  { to: "/takvim", label: "Takvim", icon: CalendarDays },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isWorkshop = user?.role === "atolye" || user?.is_impersonated;
  const nav = isAdmin ? ADMIN_NAV : USTA_NAV;
  const doLogout = async () => { await logout(); navigate("/login"); };
  return (
    <div className="min-h-screen flex bg-[--page-bg]">
      <aside className="w-60 bg-white border-r border-zinc-200 flex flex-col shrink-0" data-testid="sidebar">
        <div className="p-5 border-b border-zinc-200">
          <div className="text-xs uppercase tracking-widest text-zinc-500">Atölye</div>
          <div className="font-semibold text-lg mt-0.5">Muhasebe</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}>
              <n.icon size={16} strokeWidth={1.75} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-200">
          <div className="flex items-center gap-2 px-3 mb-2 text-xs">
            {isAdmin
              ? <ShieldCheck size={12} className="text-emerald-600 shrink-0" />
              : <HardHat size={12} className="text-amber-600 shrink-0" />}
            <div className="min-w-0">
              {isWorkshop ? (
                <>
                  <div className="text-zinc-800 truncate">
                    {user?.is_impersonated ? user?.name : "Usta seçilmedi"}
                  </div>
                  <div className="text-zinc-500 truncate">
                    Atölye Tableti{user?.atolye_name ? ` · ${user.atolye_name}` : ""}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-zinc-800 truncate">{user?.name}</div>
                  <div className="text-zinc-500 truncate">{user?.email}</div>
                </>
              )}
            </div>
          </div>
          <Button data-testid="logout-button" onClick={doLogout} variant="ghost" size="sm" className="w-full justify-start text-zinc-600">
            <LogOut size={16} className="mr-2" />Çıkış
          </Button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="p-6 md:p-8 max-w-[1400px]">{children}</div>
      </main>
      {isAdmin && <AiChatBubble />}
      {isWorkshop && <WorkshopUstaSwitcher />}
    </div>
  );
}
